import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

import { api, definirCSRF } from "./api.ts";

export type Usuario = {
  id: number;
  login: string;
  nome: string;
  papel: string;
  precisa_trocar_senha: boolean;
};

type Contexto = {
  usuario: Usuario | null;
  alertasAbertos: number;
  carregando: boolean;
  entrar: (login: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  recarregar: () => Promise<void>;
};

const Ctx = createContext<Contexto | null>(null);

export function podeEscrever(u: Usuario | null): boolean {
  return u?.papel === "admin" || u?.papel === "suporte";
}
export function ehAdmin(u: Usuario | null): boolean {
  return u?.papel === "admin";
}

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [alertasAbertos, setAlertas] = useState(0);
  const [carregando, setCarregando] = useState(true);

  const recarregar = useCallback(async () => {
    try {
      const r = await api.get<{ usuario: Usuario; csrf: string; alertas_abertos: number }>("/sessao");
      definirCSRF(r.csrf);
      setUsuario(r.usuario);
      setAlertas(r.alertas_abertos);
    } catch {
      setUsuario(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { void recarregar(); }, [recarregar]);

  const entrar = useCallback(async (login: string, senha: string) => {
    const r = await api.post<{ usuario: Usuario; csrf: string }>("/entrar", { login, senha });
    definirCSRF(r.csrf);
    setUsuario(r.usuario);
    await recarregar();
  }, [recarregar]);

  const sair = useCallback(async () => {
    await api.post("/sair").catch(() => {});
    definirCSRF("");
    setUsuario(null);
  }, []);

  return (
    <Ctx.Provider value={{ usuario, alertasAbertos, carregando, entrar, sair, recarregar }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSessao(): Contexto {
  const c = useContext(Ctx);
  if (!c) throw new Error("useSessao fora do ProvedorSessao");
  return c;
}
